import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Polyline, ZoomControl, useMap } from 'react-leaflet';
import { 
  ArrowLeft, Download, ShoppingBag, Check, 
  Frame, Type, Layers, Gift, X 
} from 'lucide-react';
import { cellToBoundary } from 'h3-js';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import html2canvas from 'html2canvas';
import './PosterStudio.css';
import { generateAllPlaceCircles, getUnionedCircleRegions, getPlaceHexagons } from './circleUtils';
import { getPlaceCenter, createPinIcon } from './pinUtils';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8005";

function PosterMapAutoFit({ bounds, orientation, aspectRatio }) {
  const map = useMap();
  const hasFitRef = useRef(false);

  useEffect(() => {
    if (!hasFitRef.current && bounds && bounds[0][0] < bounds[1][0]) {
      hasFitRef.current = true;
      map.fitBounds(bounds, { padding: [35, 35] });
    }
  }, [bounds, map]);

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 120);
    return () => clearTimeout(timer);
  }, [orientation, aspectRatio, map]);

  return null;
}

const TIERS = [
  {
    id: 'digital_free',
    frameClass: 'frame-digital',
    name: 'Low-Res Digital Art',
    subtitle: 'Watermarked digital preview',
    desc: 'Instant watermarked PDF download. Great for sharing or viewing your uncovered map.',
    price: 0.00,
    isDigital: true,
    isFree: true,
    isAvailable: true,
    badge: 'Free',
    icon: Download
  },
  {
    id: 'digital_high',
    frameClass: 'frame-digital',
    name: 'High-Res Digital Art',
    subtitle: 'Print-ready high resolution files',
    desc: 'Instant 300 DPI high-res PDF without watermark. Perfect for DIY framing or local printing.',
    price: 1.99,
    isDigital: true,
    isFree: false,
    isAvailable: false,
    badge: 'Coming soon!',
    icon: Download
  },
  {
    id: 'unframed',
    frameClass: 'frame-unframed',
    name: 'Fine Art Print Only',
    subtitle: 'Archival Museum Paper',
    desc: 'Heavyweight 250 gsm acid-free fine art paper with matte smooth finish.',
    price: null,
    isDigital: false,
    isFree: false,
    isAvailable: false,
    badge: 'Coming soon!',
    icon: Layers
  },
  {
    id: 'black',
    frameClass: 'frame-black',
    name: 'Classic Black Wood Frame',
    subtitle: 'Modern Gallery Finish',
    desc: 'Solid wood frame with matte black coating and shatterproof crystal-clear plexiglass.',
    price: null,
    isDigital: false,
    isFree: false,
    isAvailable: false,
    badge: 'Coming soon!',
    icon: Frame
  },
  {
    id: 'oak',
    frameClass: 'frame-oak',
    name: 'Premium Natural Oak Frame',
    subtitle: 'Scandinavian Solid Oak',
    desc: 'Warm sustainably-sourced natural oak wood with hand-finished beveled edges.',
    price: null,
    isDigital: false,
    isFree: false,
    isAvailable: false,
    badge: 'Coming soon!',
    icon: Frame
  },
  {
    id: 'white',
    frameClass: 'frame-white',
    name: 'Modern White Wood Frame',
    subtitle: 'Minimalist Studio Style',
    desc: 'Clean matte white wooden frame with museum-grade mounting board.',
    price: null,
    isDigital: false,
    isFree: false,
    isAvailable: false,
    badge: 'Coming soon!',
    icon: Frame
  }
];

const GIFT_PRESETS = [
  {
    name: "Couples & Romance",
    title: "DAVID & ZOE",
    subtitle: "Me & You to the end of the world",
    tagline: "OUR WORLD TRAVELS • EST. 2022",
    font: "serif"
  },
  {
    name: "Anniversary / Gift",
    title: "WHERE WE WANDER",
    subtitle: "Every step of our journey together",
    tagline: "FIVE YEARS OF ADVENTURE",
    font: "cinzel"
  },
  {
    name: "Family Heritage",
    title: "THE EXPLORERS",
    subtitle: "Making memories across continents",
    tagline: "A LIFETIME OF TRAVELS",
    font: "sans"
  },
  {
    name: "Parents' Day",
    title: "TO MOM & DAD",
    subtitle: "Every horizon you inspired us to chase",
    tagline: "WITH ALL OUR LOVE",
    font: "serif"
  },
  {
    name: "Wanderlust",
    title: "THE UNCOVERED WORLD",
    subtitle: "Not all those who wander are lost",
    tagline: "EXPLORING THE UNKNOWN",
    font: "mono"
  }
];

function PosterStudio({
  visitedHexes = {},
  places = [],
  currentUser = { name: "Explorer" },
  activeMap = null,
  onClose,
  initialMapStyle = "default",
  initialOpacity = 0.4,
  initialShowUnvisited = false
}) {
  const [activeTab, setActiveTab] = useState('text'); // 'text' | 'frame' | 'style'
  const [selectedTierId, setSelectedTierId] = useState('digital_free');
  const [showUnvisited, setShowUnvisited] = useState(initialShowUnvisited);
  
  // Custom Typography states
  const defaultTitle = activeMap?.name ? activeMap.name.toUpperCase() : (currentUser?.name ? `${currentUser.name.toUpperCase()}'S WORLD` : "MY UNCOVERED WORLD");
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(activeMap?.description || "Every step of our journey together");

  const visitedCount = useMemo(() => places.filter(p => p.status !== 'yet_to_visit').length, [places]);
  const wishlistCount = useMemo(() => places.filter(p => p.status === 'yet_to_visit').length, [places]);
  const defaultTagline = useMemo(() => {
    if (showUnvisited && wishlistCount > 0) {
      return `${visitedCount} VISITED • ${wishlistCount} ON BUCKET LIST • EST. ${new Date().getFullYear()}`;
    }
    return `${visitedCount || places.length || 0} REGIONS DISCOVERED • EST. ${new Date().getFullYear()}`;
  }, [visitedCount, wishlistCount, places.length, showUnvisited]);

  const [tagline, setTagline] = useState(defaultTagline);

  useEffect(() => {
    setTagline(defaultTagline);
  }, [defaultTagline]);

  const [fontFamily, setFontFamily] = useState('serif'); // 'serif' | 'cinzel' | 'sans' | 'mono'
  const [plaqueTheme, setPlaqueTheme] = useState('light'); // 'light' | 'dark'

  // Framing & Matting states
  const [orientation, setOrientation] = useState('portrait'); // 'portrait' | 'landscape'
  const [aspectRatio, setAspectRatio] = useState('3-4'); // '3-4' | '2-3' | '1-1'
  const [matting, setMatting] = useState('classic'); // 'none' | 'thin' | 'classic' | 'wide'
  const sanitizedInitialStyle = initialMapStyle === 'see-through' ? 'circled' : (initialMapStyle || 'default');
  const [mapStyle, setMapStyle] = useState(sanitizedInitialStyle);
  const [opacity, setOpacity] = useState(initialOpacity);

  const posterSheetRef = useRef(null);

  const initialBounds = useMemo(() => {
    if (!places || places.length === 0 || !visitedHexes || Object.keys(visitedHexes).length === 0) return null;
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    Object.values(visitedHexes).forEach(boundary => {
      boundary.forEach(([lat, lng]) => {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      });
    });
    if (minLat < maxLat && minLng < maxLng) {
      return [[minLat, minLng], [maxLat, maxLng]];
    }
    return null;
  }, [visitedHexes, places]);

  const getDimensionLabel = () => {
    if (aspectRatio === '1-1') return '20×20"';
    if (orientation === 'landscape') {
      return aspectRatio === '3-4' ? '24×18"' : '36×24"';
    }
    return aspectRatio === '3-4' ? '18×24"' : '24×36"';
  };

  const getDimensionSpecText = () => {
    if (aspectRatio === '1-1') return '20×20 in (50×50 cm)';
    if (orientation === 'landscape') {
      return aspectRatio === '3-4' ? '24×18 in (60×45 cm)' : '36×24 in (90×60 cm)';
    }
    return aspectRatio === '3-4' ? '18×24 in (45×60 cm)' : '24×36 in (60×90 cm)';
  };

  // Modal state
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  const selectedTier = useMemo(() => {
    return TIERS.find(t => t.id === selectedTierId) || TIERS[0];
  }, [selectedTierId]);

  const invertedPositions = useMemo(() => [
    [
      [90, -180],
      [90, 180],
      [-90, 180],
      [-90, -180]
    ],
    ...Object.values(visitedHexes)
  ], [visitedHexes]);

  const visitedPlaceCircles = useMemo(() => {
    return generateAllPlaceCircles(places, visitedHexes, 'visited');
  }, [places, visitedHexes]);

  const unvisitedPlaceCircles = useMemo(() => {
    if (!showUnvisited) return [];
    return generateAllPlaceCircles(places, visitedHexes, 'yet_to_visit');
  }, [places, visitedHexes, showUnvisited]);

  const unionedVisitedRegions = useMemo(() => {
    return getUnionedCircleRegions(visitedPlaceCircles);
  }, [visitedPlaceCircles]);

  const unionedUnvisitedRegions = useMemo(() => {
    return getUnionedCircleRegions(unvisitedPlaceCircles);
  }, [unvisitedPlaceCircles]);

  const invertedCirclePositions = useMemo(() => [
    [
      [90, -180],
      [90, 180],
      [-90, 180],
      [-90, -180]
    ],
    ...unionedVisitedRegions.holes
  ], [unionedVisitedRegions]);

  const unvisitedHexBoundaries = useMemo(() => {
    if (!showUnvisited) return [];
    const hexMap = new Map();
    places.filter(p => p.status === 'yet_to_visit').forEach(place => {
      const hexes = getPlaceHexagons(place);
      hexes.forEach(h => {
        if (!hexMap.has(h)) {
          hexMap.set(h, {
            hex: h,
            boundary: cellToBoundary(h),
            place
          });
        }
      });
    });
    return Array.from(hexMap.values());
  }, [places, showUnvisited]);

  const handleSelectStyle = (newStyle) => {
    setMapStyle(newStyle);
    if (newStyle === 'pins' || newStyle === 'pins-label') {
      setOpacity(0.0);
    } else if (newStyle === 'circled') {
      setOpacity(0.8);
    } else if (newStyle === 'default') {
      setOpacity(0.4);
    }
  };

  const applyPreset = (preset) => {
    setTitle(preset.title);
    setSubtitle(preset.subtitle);
    setTagline(preset.tagline);
    setFontFamily(preset.font);
  };

  const handleStartOrder = () => {
    setIsOrderModalOpen(true);
    setGenerationSuccess(false);
    setGenerationError(null);
  };

  const handleDownloadFreePdf = async () => {
    if (!posterSheetRef.current) return;
    setIsGenerating(true);
    setGenerationError(null);
    setGenerationSuccess(false);

    try {
      const canvas = await html2canvas(posterSheetRef.current, {
        useCORS: true,
        allowTaint: false,
        scale: 1.5,
        backgroundColor: plaqueTheme === 'dark' ? '#0d1117' : '#fbfbf9',
        ignoreElements: (element) => {
          return (
            element.classList?.contains('leaflet-control-zoom') ||
            element.classList?.contains('leaflet-control-container')
          );
        }
      });

      const imageData = canvas.toDataURL('image/jpeg', 0.90);

      const response = await fetch(`${API_URL}/export/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_data: imageData,
          title: title || 'MY UNCOVERED WORLD',
          subtitle: subtitle || '',
          tagline: tagline || '',
          orientation: orientation,
          aspect_ratio: aspectRatio,
          is_free: true,
          watermark: true,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Export failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const cleanTitle = (title || 'poster').replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
      link.download = `fog_of_life_${cleanTitle}_preview.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setGenerationSuccess(true);
    } catch (err) {
      console.error('PDF export error:', err);
      setGenerationError(err.message || 'Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="poster-studio-root">
      {/* Studio Header */}
      <header className="studio-header">
        <div className="studio-header-left">
          <button className="studio-back-btn" onClick={onClose}>
            <ArrowLeft size={16} /> Back to Map Editor
          </button>
          <div className="studio-title-badge">
            <h2>Poster Studio</h2>
            <span className="studio-badge">Print Preview</span>
          </div>
        </div>

        <div className="studio-header-right">
          <div className="studio-price-pill">
            <span>{selectedTier.name}:</span>
            {selectedTier.isFree ? (
              <strong className="price-free">Free</strong>
            ) : selectedTier.price !== null ? (
              <>
                <strong>${selectedTier.price.toFixed(2)}</strong>
                <span className="pill-coming-soon-label">Coming soon!</span>
              </>
            ) : (
              <strong className="pill-coming-soon-label">Coming soon!</strong>
            )}
          </div>
          <button className="studio-order-btn" onClick={handleStartOrder}>
            {selectedTier.isDigital ? <Download size={16} /> : <ShoppingBag size={16} />}
            {selectedTier.isFree
              ? 'Download Free PDF'
              : selectedTier.price !== null
              ? `High-Res Download ($${selectedTier.price.toFixed(2)})`
              : 'Order Physical Print'}
          </button>
        </div>
      </header>

      {/* Main Studio Body */}
      <div className="studio-body">
        {/* Center Stage Mockup Canvas */}
        <main className="studio-stage">
          <div className="studio-stage-hint">
            Tip: You can pan and zoom the map inside the frame to adjust your composition
          </div>

          <div className={`mockup-frame ${selectedTier.frameClass} orientation-${orientation} ratio-${aspectRatio}`}>
            {/* Passe-Partout / Matting */}
            <div className={`mockup-matting matting-${matting}`}>
              {/* Poster Paper Sheet */}
              <div ref={posterSheetRef} className={`poster-sheet theme-${plaqueTheme}`}>
                
                {/* Embedded Map Area */}
                <div className="poster-map-wrapper">
                  <MapContainer
                    center={[40.7128, -74.0060]}
                    zoom={12}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                    attributionControl={false}
                  >
                    <ZoomControl position="topright" />
                    <PosterMapAutoFit bounds={initialBounds} orientation={orientation} aspectRatio={aspectRatio} />

                    {mapStyle === "pins" || mapStyle === "pins-label" ? (
                      <>
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                          attribution='Tiles &copy; Esri'
                          maxZoom={16}
                        />
                        {places.map((place) => {
                          const isVisited = place.status !== 'yet_to_visit';
                          if (!isVisited && !showUnvisited) return null;
                          const center = getPlaceCenter(place);
                          if (!center) return null;
                          return (
                            <Marker
                              key={`poster-pin-${place.id}`}
                              position={center}
                              icon={createPinIcon(place.name, mapStyle === "pins-label", place.custom_label, isVisited ? 'red' : 'blue')}
                              title={place.custom_label ? `${place.custom_label} (${place.name})` : place.name}
                            />
                          );
                        })}
                      </>
                    ) : mapStyle === "default" ? (
                      <>
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                          attribution='Tiles &copy; Esri'
                          maxZoom={16}
                        />
                        {/* Visited hexagons (red color matching visited pins) */}
                        {Object.entries(visitedHexes).map(([h3Index, boundary]) => (
                          <Polygon
                            key={`poster-fill-${h3Index}`}
                            positions={boundary}
                            pathOptions={{ color: 'rgba(239, 68, 68, 0.35)', weight: 0.8, fillColor: '#ef4444', fillOpacity: 0.28, interactive: false }}
                          />
                        ))}
                        {/* Inverted dark fog mask cutout around visited hexes */}
                        <Polygon
                          positions={invertedPositions}
                          pathOptions={{ color: '#ef4444', weight: 1.2, fillColor: '#000000', fillOpacity: opacity, interactive: false }}
                        />
                        {/* Unvisited mini hexagons (blue color matching unvisited pins, veiled under fog) */}
                        {showUnvisited && unvisitedHexBoundaries.map(({ hex, boundary, place }) => (
                          <Polygon
                            key={`poster-unvisited-hex-${place.id}-${hex}`}
                            positions={boundary}
                            pathOptions={{
                              color: '#3b82f6',
                              weight: 1.3,
                              opacity: 0.9,
                              fillColor: '#1d4ed8',
                              fillOpacity: Math.min(opacity * 0.35, 0.35),
                              interactive: false
                            }}
                          />
                        ))}
                      </>
                    ) : (
                      /* circled (Hand-Drawn Circles) */
                      <>
                        {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                          <ReactLeafletGoogleLayer 
                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} 
                            type={'roadmap'} 
                          />
                        ) : (
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap contributors'
                          />
                        )}
                        {opacity > 0 && (
                          <Polygon
                            positions={invertedCirclePositions}
                            pathOptions={{ color: 'transparent', fillColor: '#000000', fillOpacity: opacity, interactive: false }}
                          />
                        )}
                        {/* Visited hand-drawn circles: red pen outlines */}
                        {unionedVisitedRegions.strokes.map((stroke, idx) => (
                          <React.Fragment key={`poster-circle-stroke-${idx}`}>
                            {/* Subtle ink absorption bleed */}
                            <Polyline
                              positions={stroke}
                              pathOptions={{ color: '#b91c1c', weight: 4.5, opacity: 0.22, lineCap: 'round', lineJoin: 'round', interactive: false }}
                            />
                            {/* Human-drawn pen stroke */}
                            <Polyline
                              positions={stroke}
                              pathOptions={{ color: '#e63946', weight: 2.6, opacity: 0.92, lineCap: 'round', lineJoin: 'round', interactive: false }}
                            />
                          </React.Fragment>
                        ))}
                        {/* Unvisited circles: blue hand-drawn circles, opaque following map opacity */}
                        {showUnvisited && unionedUnvisitedRegions.holes.map((hole, idx) => (
                          <Polygon
                            key={`poster-unvisited-circle-fill-${idx}`}
                            positions={hole}
                            pathOptions={{
                              color: 'transparent',
                              fillColor: '#1d4ed8',
                              fillOpacity: Math.min(opacity * 0.35, 0.35),
                              interactive: false
                            }}
                          />
                        ))}
                        {showUnvisited && unionedUnvisitedRegions.strokes.map((stroke, idx) => (
                          <React.Fragment key={`poster-unvisited-circle-stroke-${idx}`}>
                            {/* Subtle blue ink bleed */}
                            <Polyline
                              positions={stroke}
                              pathOptions={{ color: '#1d4ed8', weight: 4.5, opacity: 0.25, lineCap: 'round', lineJoin: 'round', interactive: false }}
                            />
                            {/* Human-drawn blue pen stroke */}
                            <Polyline
                              positions={stroke}
                              pathOptions={{ color: '#3b82f6', weight: 2.6, opacity: 0.95, lineCap: 'round', lineJoin: 'round', interactive: false }}
                            />
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </MapContainer>
                  <div className="poster-map-inner-border" />
                </div>

                {/* Typography Plaque / Dedication */}
                <div className={`poster-plaque font-${fontFamily}`}>
                  <h3 className="poster-title">{title || "CUSTOM TRAVEL PRINT"}</h3>
                  <div className="poster-divider">
                    <span className="poster-divider-icon">◆</span>
                  </div>
                  {subtitle && <p className="poster-subtitle">{subtitle}</p>}
                  {tagline && <p className="poster-tagline">{tagline}</p>}
                </div>

              </div>
            </div>
          </div>
        </main>

        {/* Customization Sidebar (Right Side) */}
        <aside className="studio-sidebar">
          {/* Tabs */}
          <div className="studio-tabs">
            <button
              className={`studio-tab-btn ${activeTab === 'text' ? 'active' : ''}`}
              onClick={() => setActiveTab('text')}
            >
              <Type size={16} /> Text & Gifts
            </button>
            <button
              className={`studio-tab-btn ${activeTab === 'frame' ? 'active' : ''}`}
              onClick={() => setActiveTab('frame')}
            >
              <Frame size={16} /> Frame & Size
            </button>
            <button
              className={`studio-tab-btn ${activeTab === 'style' ? 'active' : ''}`}
              onClick={() => setActiveTab('style')}
            >
              <Layers size={16} /> Map Style
            </button>
          </div>

          {/* Tab Content */}
          <div className="studio-tab-content">
            {activeTab === 'text' && (
              <>
                {/* Gift Presets */}
                <div className="option-group">
                  <div className="option-group-label">
                    <span>Gift Ideas & Inspiration</span>
                    <Gift size={14} color="#58a6ff" />
                  </div>
                  <div className="preset-chips">
                    {GIFT_PRESETS.map((preset, idx) => (
                      <button
                        key={idx}
                        className="preset-chip"
                        onClick={() => applyPreset(preset)}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div className="option-group">
                  <label className="option-group-label">Main Title</label>
                  <input
                    type="text"
                    className="studio-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. DAVID & ZOE ON THEIR TRAVELS"
                    maxLength={48}
                  />
                </div>

                {/* Subtitle / Dedication */}
                <div className="option-group">
                  <label className="option-group-label">Subtitle / Dedication Note</label>
                  <input
                    type="text"
                    className="studio-input"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="e.g. Me & You to the end of the world"
                    maxLength={64}
                  />
                </div>

                {/* Tagline / Coordinates */}
                <div className="option-group">
                  <label className="option-group-label">Coordinates or Tagline</label>
                  <input
                    type="text"
                    className="studio-input"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="e.g. 14 REGIONS UNCOVERED • EST. 2026"
                    maxLength={64}
                  />
                </div>

                {/* Typography Style */}
                <div className="option-group">
                  <label className="option-group-label">Typography Style</label>
                  <div className="segmented-control">
                    <button
                      className={`segmented-btn ${fontFamily === 'serif' ? 'active' : ''}`}
                      onClick={() => setFontFamily('serif')}
                    >
                      Classic Serif
                    </button>
                    <button
                      className={`segmented-btn ${fontFamily === 'cinzel' ? 'active' : ''}`}
                      onClick={() => setFontFamily('cinzel')}
                    >
                      Cinzel Luxury
                    </button>
                    <button
                      className={`segmented-btn ${fontFamily === 'sans' ? 'active' : ''}`}
                      onClick={() => setFontFamily('sans')}
                    >
                      Modern Sans
                    </button>
                    <button
                      className={`segmented-btn ${fontFamily === 'mono' ? 'active' : ''}`}
                      onClick={() => setFontFamily('mono')}
                    >
                      Monospace
                    </button>
                  </div>
                </div>

                {/* Plaque Color Theme */}
                <div className="option-group">
                  <label className="option-group-label">Plaque Paper Style</label>
                  <div className="segmented-control">
                    <button
                      className={`segmented-btn ${plaqueTheme === 'light' ? 'active' : ''}`}
                      onClick={() => setPlaqueTheme('light')}
                    >
                      Museum White Paper
                    </button>
                    <button
                      className={`segmented-btn ${plaqueTheme === 'dark' ? 'active' : ''}`}
                      onClick={() => setPlaqueTheme('dark')}
                    >
                      Midnight Dark Paper
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'frame' && (
              <>
                {/* Framing Tiers */}
                <div className="option-group">
                  <div className="option-group-label">
                    <span>Product Format & Frame</span>
                  </div>
                  <div className="tier-cards-grid">
                    {TIERS.map((tier) => {
                      const IconComp = tier.icon;
                      const isSelected = selectedTierId === tier.id;
                      return (
                        <div
                          key={tier.id}
                          className={`tier-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedTierId(tier.id)}
                        >
                          <div className="tier-card-left">
                            <div className="tier-icon-box">
                              <IconComp size={18} />
                            </div>
                            <div>
                              <div className="tier-title">{tier.name}</div>
                              <div className="tier-desc">{tier.subtitle}</div>
                            </div>
                          </div>
                          <div className="tier-card-right">
                            {tier.isFree ? (
                              <>
                                <div className="tier-price tier-price-free">Free</div>
                                <span className="tier-badge-free">Ready</span>
                              </>
                            ) : tier.price !== null ? (
                              <>
                                <div className="tier-price">${tier.price.toFixed(2)}</div>
                                <span className="tier-badge-coming-soon">Coming soon!</span>
                              </>
                            ) : (
                              <div className="tier-price tier-price-coming-soon">Coming soon!</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Orientation Toggle */}
                <div className="option-group">
                  <label className="option-group-label">Poster Orientation</label>
                  <div className="segmented-control">
                    <button
                      className={`segmented-btn ${orientation === 'portrait' ? 'active' : ''}`}
                      onClick={() => setOrientation('portrait')}
                    >
                      Portrait (Vertical)
                    </button>
                    <button
                      className={`segmented-btn ${orientation === 'landscape' ? 'active' : ''}`}
                      onClick={() => setOrientation('landscape')}
                    >
                      Landscape (Horizontal)
                    </button>
                  </div>
                </div>

                {/* Aspect Ratio */}
                <div className="option-group">
                  <label className="option-group-label">Print Dimensions ({orientation === 'landscape' ? 'Horizontal' : 'Vertical'})</label>
                  <div className="segmented-control">
                    <button
                      className={`segmented-btn ${aspectRatio === '3-4' ? 'active' : ''}`}
                      onClick={() => setAspectRatio('3-4')}
                    >
                      {orientation === 'landscape' ? '24×18" (4:3)' : '18×24" (3:4)'}
                    </button>
                    <button
                      className={`segmented-btn ${aspectRatio === '2-3' ? 'active' : ''}`}
                      onClick={() => setAspectRatio('2-3')}
                    >
                      {orientation === 'landscape' ? '36×24" (3:2)' : '24×36" (2:3)'}
                    </button>
                    <button
                      className={`segmented-btn ${aspectRatio === '1-1' ? 'active' : ''}`}
                      onClick={() => setAspectRatio('1-1')}
                    >
                      20×20" (1:1)
                    </button>
                  </div>
                </div>

                {/* Passe-Partout / Matting */}
                <div className="option-group">
                  <label className="option-group-label">Gallery Matting (Passe-Partout)</label>
                  <div className="segmented-control">
                    <button
                      className={`segmented-btn ${matting === 'none' ? 'active' : ''}`}
                      onClick={() => setMatting('none')}
                    >
                      Full Bleed
                    </button>
                    <button
                      className={`segmented-btn ${matting === 'thin' ? 'active' : ''}`}
                      onClick={() => setMatting('thin')}
                    >
                      Thin (1")
                    </button>
                    <button
                      className={`segmented-btn ${matting === 'classic' ? 'active' : ''}`}
                      onClick={() => setMatting('classic')}
                    >
                      Classic (2")
                    </button>
                    <button
                      className={`segmented-btn ${matting === 'wide' ? 'active' : ''}`}
                      onClick={() => setMatting('wide')}
                    >
                      Wide Gallery
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'style' && (
              <>
                <div className="option-group">
                  <label className="option-group-label">Map Presentation Style</label>
                  <div className="segmented-control grid-styles">
                    <button
                      className={`segmented-btn ${mapStyle === 'pins' ? 'active' : ''}`}
                      onClick={() => handleSelectStyle('pins')}
                    >
                      Map Pins
                    </button>
                    <button
                      className={`segmented-btn ${mapStyle === 'pins-label' ? 'active' : ''}`}
                      onClick={() => handleSelectStyle('pins-label')}
                    >
                      Pins with Label
                    </button>
                    <button
                      className={`segmented-btn ${mapStyle === 'circled' ? 'active' : ''}`}
                      onClick={() => handleSelectStyle('circled')}
                    >
                      Hand-Drawn Circles
                    </button>
                    <button
                      className={`segmented-btn ${mapStyle === 'default' ? 'active' : ''}`}
                      onClick={() => handleSelectStyle('default')}
                    >
                      Dark Fog
                    </button>
                  </div>
                </div>

                <div className="option-group">
                  <label className="option-group-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                    <span>Include Unvisited Places (Bucket List)</span>
                    <input
                      type="checkbox"
                      checked={showUnvisited}
                      onChange={(e) => setShowUnvisited(e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }}
                    />
                  </label>
                </div>

                {mapStyle !== 'pins' && mapStyle !== 'pins-label' && (
                  <div className="option-group">
                    <label className="option-group-label">
                      <span>Fog Darkness ({Math.round(opacity * 100)}%)</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(opacity * 100)}
                      onChange={(e) => setOpacity(parseInt(e.target.value) / 100)}
                      className="resolution-slider"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Bottom Checkout / Order Footer */}
          <div className="studio-checkout-footer">
            <div className="studio-checkout-summary">
              <span>{selectedTier.name} ({getDimensionLabel()})</span>
              <span className="total-val">
                {selectedTier.isFree
                  ? 'Free'
                  : selectedTier.price !== null
                  ? `$${selectedTier.price.toFixed(2)}`
                  : 'Coming soon!'}
              </span>
            </div>
            <button className="studio-order-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={handleStartOrder}>
              {selectedTier.isDigital ? <Download size={18} /> : <ShoppingBag size={18} />}
              {selectedTier.isFree
                ? 'Download Free PDF (Watermarked)'
                : selectedTier.price !== null
                ? `High-Res Print ($${selectedTier.price.toFixed(2)} - Coming Soon)`
                : `Order Physical Print (Coming Soon)`}
            </button>
          </div>
        </aside>
      </div>

      {/* Order / Download Preview Modal */}
      {isOrderModalOpen && (
        <div className="studio-modal-overlay" onClick={() => setIsOrderModalOpen(false)}>
          <div className="studio-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="studio-modal-header">
              <h3>
                {selectedTier.isDigital ? <Download size={22} color="#58a6ff" /> : <ShoppingBag size={22} color="#58a6ff" />}
                {selectedTier.isFree
                  ? 'Download Free Digital Art (PDF)'
                  : selectedTier.isDigital
                  ? 'High-Resolution Digital Art ($1.99)'
                  : 'Order Physical Print'}
              </h3>
              <button className="studio-modal-close" onClick={() => setIsOrderModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="order-spec-list">
              <div className="order-spec-row">
                <span>Product Tier:</span>
                <span>{selectedTier.name}</span>
              </div>
              <div className="order-spec-row">
                <span>Dimensions:</span>
                <span>{getDimensionSpecText()}</span>
              </div>
              <div className="order-spec-row">
                <span>Print Title:</span>
                <span>"{title}"</span>
              </div>
              {subtitle && (
                <div className="order-spec-row">
                  <span>Dedication:</span>
                  <span>"{subtitle}"</span>
                </div>
              )}
              <div className="order-spec-row">
                <span>Resolution / License:</span>
                <span>{selectedTier.isFree ? 'Watermarked Digital Preview (PDF)' : '300 DPI Fine Art Grade'}</span>
              </div>
              <div className="order-spec-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <span style={{ color: '#fff', fontWeight: 700 }}>Total Price:</span>
                <span style={{ color: selectedTier.isFree ? '#3fb950' : '#58a6ff', fontSize: '16px', fontWeight: 700 }}>
                  {selectedTier.isFree
                    ? 'Free ($0.00 USD)'
                    : selectedTier.price !== null
                    ? `$${selectedTier.price.toFixed(2)} USD`
                    : 'Coming soon!'}
                </span>
              </div>
            </div>

            {selectedTier.isFree ? (
              <div>
                <p style={{ fontSize: '13px', color: '#8b949e', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                  Download a digital PDF of your uncovered world map at your selected aspect ratio. This free preview includes subtle diagonal watermarks.
                </p>

                {generationError && (
                  <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', borderRadius: '8px', padding: '12px', marginBottom: '14px', color: '#f87171', fontSize: '13px' }}>
                    {generationError}
                  </div>
                )}

                {generationSuccess ? (
                  <div style={{ background: 'rgba(46, 160, 67, 0.15)', border: '1px solid #2ea043', borderRadius: '8px', padding: '16px', textAlign: 'center', color: '#3fb950' }}>
                    <Check size={28} style={{ margin: '0 auto 6px auto', display: 'block' }} />
                    <strong style={{ fontSize: '15px' }}>PDF Downloaded Successfully!</strong>
                    <p style={{ fontSize: '12px', margin: '6px 0 14px 0', color: '#c9d1d9' }}>
                      Your watermarked PDF poster has been generated and downloaded to your computer.
                    </p>
                    <button 
                      className="order-action-btn"
                      onClick={handleDownloadFreePdf}
                      disabled={isGenerating}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' }}
                    >
                      <Download size={16} /> Download Again
                    </button>
                  </div>
                ) : (
                  <button 
                    className="order-action-btn" 
                    onClick={handleDownloadFreePdf} 
                    disabled={isGenerating}
                    style={{ width: '100%' }}
                  >
                    {isGenerating ? (
                      <>Generating Watermarked PDF...</>
                    ) : (
                      <><Download size={18} /> Generate & Download Free PDF</>
                    )}
                  </button>
                )}
              </div>
            ) : selectedTier.id === 'digital_high' ? (
              <div>
                <div style={{ background: 'rgba(88, 166, 255, 0.1)', border: '1px solid rgba(88, 166, 255, 0.3)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                  <span style={{ color: '#58a6ff', fontWeight: 600, fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                    🚀 High-Res Digital Art Coming Soon ($1.99)
                  </span>
                  <p style={{ fontSize: '13px', color: '#c9d1d9', margin: 0, lineHeight: 1.5 }}>
                    We're finalizing our unwatermarked 300 DPI render pipeline for crystal-clear large format home printing. In the meantime, you can download the free watermarked version right now!
                  </p>
                </div>
                <button 
                  className="order-action-btn" 
                  onClick={() => {
                    setSelectedTierId('digital_free');
                    setGenerationSuccess(false);
                    setGenerationError(null);
                  }}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  <Download size={18} /> Switch to Free Watermarked Download
                </button>
                <button
                  onClick={() => setIsOrderModalOpen(false)}
                  style={{ width: '100%', padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#8b949e', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div style={{ background: 'rgba(210, 153, 34, 0.1)', border: '1px solid rgba(210, 153, 34, 0.3)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                  <span style={{ color: '#e3b341', fontWeight: 600, fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                    📦 Fine Art Framing Coming Soon!
                  </span>
                  <p style={{ fontSize: '13px', color: '#c9d1d9', margin: 0, lineHeight: 1.5 }}>
                    We are currently finalizing partnerships with museum-grade printing and custom framing studios. You can still test your frame mockups in the studio and download a free digital preview of your map!
                  </p>
                </div>
                <button 
                  className="order-action-btn" 
                  onClick={() => {
                    setSelectedTierId('digital_free');
                    setGenerationSuccess(false);
                    setGenerationError(null);
                  }}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  <Download size={18} /> Switch to Free Watermarked Download
                </button>
                <button
                  onClick={() => setIsOrderModalOpen(false)}
                  style={{ width: '100%', padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#8b949e', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PosterStudio;
